/**
 * Update Deal Handler
 * Edit handler for updating deal information
 */

import type { ToolExecutionContext } from '../../types/index.js';
import type { EditPreview } from '../../types/index.js';
import { EditHandlerBase } from '../base/index.js';
import { uuidField, objectField } from '../../utils/schema-builder.js';
import { generateDiff, hasChanges } from '../../utils/diff-generator.js';
import { generateConfirmationToken } from '../../utils/confirmation-token.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';
import { validateUpdateDealInput, validateDealId, validateStageTransition } from '../../utils/deal-validation.js';

/**
 * Update deal handler
 * Fetches current deal via GraphQL and generates preview with RLS enforcement
 */
export class UpdateDealHandler extends EditHandlerBase<any> {
  id = 'update_deal';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the deal to update'),
      updates: objectField(
        'Fields to update (title, stage, amount, probability, close_date, tags, assigned_users, etc.). ' +
          'assigned_users is the full replacement set of owner/assignee user ids — resolve each via find_employee first; these are Hub app_users ids (uuids), NOT contact ids.'
      ),
    },
    required: ['id', 'updates'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    // Validate input
    const id = validateDealId(params.id);
    const updates = validateUpdateDealInput(params.updates);

    if (!context?.userToken) {
      throw new Error('User token is required for deal update');
    }

    try {
      const { GetDealDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Fetch current deal
      const result = await client.query({
        query: GetDealDocument,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const currentDeal = data?.dealsCollection?.edges?.[0]?.node;
      
      if (!currentDeal) {
        throw new Error(`Deal with ID "${id}" not found or you don't have permission to edit it`);
      }

      // Validate stage transitions if stage is being updated
      if (updates.stage && updates.stage !== currentDeal.stage) {
        validateStageTransition(currentDeal.stage, updates.stage);
      }

      // Generate diff: compare current to merged (patch semantics — only changed fields)
      const merged = { ...currentDeal, ...updates };
      const changes = generateDiff(currentDeal, merged);

      if (!hasChanges(changes)) {
        throw new Error('No changes detected');
      }

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'deal',
        entityId: id,
        changes: updates,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'deal',
        id,
        changes,
        confirmationToken: token,
        preview: merged,
      };
    } catch (error) {
      throw new Error(`Deal update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
